/**
 * Où vit la progression de campagne.
 *
 * ## `localStorage` est interdit, et ce n'est pas un caprice
 *
 * Le § 5.3 le proscrit pour tout ce qui doit survivre : l'OS peut le purger sans
 * prévenir, et une campagne représente des heures de jeu. On passe donc par un
 * **coffre** abstrait, exactement comme `sauvegarde.ts` rend une chaîne sans choisir
 * sa destination :
 *
 *   - **web** : IndexedDB, qui est la seule réserve du navigateur que
 *     `navigator.storage.persist()` protège d'un nettoyage automatique ;
 *   - **natif** : `@capacitor/preferences` le jour du lot 9 — une implémentation de
 *     plus de cette interface, et rien d'autre à changer ;
 *   - **tests** : la mémoire.
 *
 * ## La seule promesse : ne jamais bloquer le jeu
 *
 * Un coffre peut être absent (navigation privée), plein (quota), ou contenir des
 * octets périmés. Aucun des trois n'empêche de jouer : `chargerProgression` rend
 * toujours une progression, `sauverProgression` ne lève jamais. Une exception dans un
 * gestionnaire de clic React couperait l'écran pour un défaut qui laisse pourtant le
 * jeu parfaitement jouable.
 */

import {
  deserialiserProgression, progressionNeuve, serialiserProgression,
  type Progression,
} from "@zinc421/shared";

import { langueValide, type Langue } from "../i18n/index.js";
import {
  deserialiserReprise, serialiserReprise, type EnCours,
} from "../jeu/reprise.js";

export interface Coffre {
  readonly lire: (cle: string) => Promise<string | null>;
  readonly ecrire: (cle: string, valeur: string) => Promise<void>;
  readonly effacer: (cle: string) => Promise<void>;
}

/** La clé de la progression. Une seule, partagée par la lecture et l'écriture. */
export const CLE_PROGRESSION = "campagne/progression";

/** La clé du réglage de son. Il commande aussi l'haptique — voir `son/haptique.ts`. */
export const CLE_SON = "reglages/son";

/** La clé de la partie en cours, celle qu'on propose de reprendre. */
export const CLE_REPRISE = "partie/en-cours";

/** La clé de la langue choisie. Absente = on suit le navigateur. */
export const CLE_LANGUE = "reglages/langue";

const BASE = "zinc421";
const MAGASIN = "coffre";

export function coffreMemoire(depart: Readonly<Record<string, string>> = {}): Coffre {
  const contenu = new Map(Object.entries(depart));
  return {
    lire: (cle) => Promise.resolve(contenu.get(cle) ?? null),
    ecrire: (cle, valeur) => { contenu.set(cle, valeur); return Promise.resolve(); },
    effacer: (cle) => { contenu.delete(cle); return Promise.resolve(); },
  };
}

/**
 * Le coffre du navigateur.
 *
 * IndexedDB est asynchrone et verbeux, mais c'est la seule réserve durable du web.
 * On demande au passage la persistance — sans jamais attendre la réponse : elle peut
 * demander une autorisation à l'utilisateur, et faire attendre un écran de menu pour
 * un confort de stockage serait le mauvais échange.
 */
export function coffreNavigateur(): Coffre {
  if (typeof indexedDB === "undefined") return coffreMemoire();

  void navigator.storage?.persist?.().catch(() => undefined);

  const ouvrir = () => new Promise<IDBDatabase>((resoudre, rejeter) => {
    const requete = indexedDB.open(BASE, 1);
    requete.onupgradeneeded = () => {
      if (!requete.result.objectStoreNames.contains(MAGASIN)) {
        requete.result.createObjectStore(MAGASIN);
      }
    };
    requete.onsuccess = () => { resoudre(requete.result); };
    requete.onerror = () => { rejeter(requete.error ?? new Error("IndexedDB refusé")); };
    // Une autre page qui bloque une migration : mieux vaut échouer que rester
    // suspendu, l'appelant sait retomber sur une progression neuve.
    requete.onblocked = () => { rejeter(new Error("IndexedDB bloqué par un autre onglet")); };
  });

  const transiger = async <T>(
    mode: IDBTransactionMode, action: (magasin: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> => {
    const base = await ouvrir();
    try {
      return await new Promise<T>((resoudre, rejeter) => {
        const transaction = base.transaction(MAGASIN, mode);
        const requete = action(transaction.objectStore(MAGASIN));
        requete.onsuccess = () => { resoudre(requete.result); };
        requete.onerror = () => { rejeter(requete.error ?? new Error("écriture refusée")); };
      });
    } finally {
      base.close();
    }
  };

  return {
    lire: async (cle) => {
      const valeur = await transiger<unknown>("readonly", (m) => m.get(cle));
      return typeof valeur === "string" ? valeur : null;
    },
    ecrire: async (cle, valeur) => {
      await transiger("readwrite", (m) => m.put(valeur, cle));
    },
    effacer: async (cle) => {
      await transiger("readwrite", (m) => m.delete(cle));
    },
  };
}

/**
 * La progression retenue, ou une neuve.
 *
 * Ne lève jamais : un coffre fermé ou une sauvegarde périmée rendent une campagne
 * vierge. C'est un choix — perdre la progression est fâcheux, ne plus pouvoir ouvrir
 * l'écran de campagne le serait davantage.
 */
export async function chargerProgression(coffre: Coffre): Promise<Progression> {
  try {
    const brut = await coffre.lire(CLE_PROGRESSION);
    if (brut === null) return progressionNeuve();
    return deserialiserProgression(brut);
  } catch {
    return progressionNeuve();
  }
}

/** Écrit la progression. Rend `false` si le coffre a refusé — jamais d'exception. */
export async function sauverProgression(
  coffre: Coffre, progression: Progression,
): Promise<boolean> {
  try {
    await coffre.ecrire(CLE_PROGRESSION, serialiserProgression(progression));
    return true;
  } catch {
    return false;
  }
}

// ------------------------------------------------------------ le son

/**
 * Le son est-il actif ?
 *
 * **Actif par défaut**, et ce n'est pas anodin : le jeu est fait pour être joué au
 * comptoir, et un 421 qui ne fait pas de bruit n'est pas un 421. Le navigateur, de
 * toute façon, ne laissera rien sonner avant le premier geste du joueur — le réglage
 * par défaut ne surprend donc personne au chargement.
 *
 * Ne lève jamais : un coffre fermé rend simplement le réglage par défaut.
 */
export async function chargerSon(coffre: Coffre): Promise<boolean> {
  try {
    const brut = await coffre.lire(CLE_SON);
    return brut === null ? true : brut === "1";
  } catch {
    return true;
  }
}

/** Retient le réglage de son. Rend `false` si le coffre a refusé — jamais d'exception. */
export async function sauverSon(coffre: Coffre, actif: boolean): Promise<boolean> {
  try {
    await coffre.ecrire(CLE_SON, actif ? "1" : "0");
    return true;
  } catch {
    return false;
  }
}

// ------------------------------------------------- la partie en cours

/**
 * La partie qu'on avait laissée en plan, ou `null`.
 *
 * Ne lève jamais, et ne devine jamais : `deserialiserReprise` refuse tout ce qui
 * n'est pas certain (§ voir `jeu/reprise.ts`), et un refus se lit ici comme une
 * absence — le joueur voit simplement un menu sans bouton « Reprendre ».
 */
export async function chargerReprise(coffre: Coffre): Promise<EnCours | null> {
  try {
    const brut = await coffre.lire(CLE_REPRISE);
    return brut === null ? null : deserialiserReprise(brut);
  } catch {
    return null;
  }
}

/** Retient la partie en cours. Rend `false` si le coffre a refusé — jamais d'exception. */
export async function sauverReprise(coffre: Coffre, enCours: EnCours): Promise<boolean> {
  try {
    await coffre.ecrire(CLE_REPRISE, serialiserReprise(enCours));
    return true;
  } catch {
    return false;
  }
}

/**
 * Oublie la partie en cours.
 *
 * Appelé quand elle se termine — proposer de reprendre une partie finie ferait douter
 * le joueur de ce qu'il vient de gagner, et `deserialiserReprise` la refuserait de
 * toute façon : autant ne pas encombrer le coffre.
 */
export async function oublierReprise(coffre: Coffre): Promise<void> {
  try {
    await coffre.effacer(CLE_REPRISE);
  } catch {
    // Rien à faire : au pire la relecture la refusera.
  }
}

// ------------------------------------------------------------ la langue

/**
 * La langue retenue, ou `null` si le joueur n'a rien choisi.
 *
 * `null` et non « français » : tant que personne n'a tranché, c'est le NAVIGATEUR qui
 * décide, et écrire une valeur par défaut dans le coffre figerait ce choix pour
 * toujours — y compris chez quelqu'un qui change ensuite la langue de son téléphone.
 */
export async function chargerLangue(coffre: Coffre): Promise<Langue | null> {
  try {
    const brut = await coffre.lire(CLE_LANGUE);
    return langueValide(brut) ? brut : null;
  } catch {
    return null;
  }
}

/** Retient la langue choisie. Ne lève jamais. */
export async function sauverLangue(coffre: Coffre, langue: Langue): Promise<boolean> {
  try {
    await coffre.ecrire(CLE_LANGUE, langue);
    return true;
  } catch {
    return false;
  }
}
